import type { ClientUiModelOption } from '../../api/platformConfig'
import type { WorkflowStepCatalogItem } from '../../api/workflowRuns'
import {
  workflowControlOperatorLabel,
  workflowControlOperatorNeedsExpectValue,
} from './workflowControlFlow'

/** 工作流定义状态 → 中文展示 */
const WORKFLOW_DEFINITION_STATUS_LABEL: Record<string, string> = {
  draft: '草稿',
  published: '已发布',
  archived: '已归档',
}

type WorkflowStepTypeMeta = {
  label: string
  defaultName: string
  description: string
}

const WORKFLOW_STEP_TYPE_META: Record<string, WorkflowStepTypeMeta> = {
  'start.input': {
    label: '开始',
    defaultName: '开始',
    description: '接收工作流试运行输入，并把输入内容交给后续节点。',
  },
  'llm.prompt': {
    label: 'LLM 推理',
    defaultName: 'LLM 推理',
    description: '调用文本模型，根据模板预设生成故事、提示词或结构化文本。',
  },
  'image.generate': {
    label: '生图任务',
    defaultName: '生图任务',
    description: '根据上游提示词提交图片生成任务，并等待生成结果。',
  },
  'condition.check': {
    label: '条件判断',
    defaultName: '条件判断',
    description: '根据主输入连入的上一步结果，走「满足」或「不满足」分支。',
  },
  'loop.until': {
    label: '循环直到',
    defaultName: '循环直到',
    description: '根据主输入连入的上一步结果重复执行循环体，直到条件满足或达到最大轮次。',
  },
  'end.output': {
    label: '结束',
    defaultName: '结束',
    description: '汇总上游节点产物，返回工作流运行结果。',
  },
  'text.rewrite': {
    label: '文本整理',
    defaultName: '文本整理',
    description: '对输入文本进行整理、改写或补充，产出下游可复用内容。',
  },
  'storyboard.draft': {
    label: '分镜草稿',
    defaultName: '分镜草稿',
    description: '把剧情拆成分镜草案，便于后续绘制和审查。',
  },
  'comic.project.create': {
    label: '创建漫画',
    defaultName: '创建漫画',
    description: '将结构化规划直接创建为漫画项目，并绑定到后续节点。',
  },
  'comic.episode.generate': {
    label: '漫画自动生图',
    defaultName: '漫画自动生图',
    description: '复用漫画话级生图队列，自动生成并回写目标话次。',
  },
}

const WORKFLOW_STEP_CATEGORY_LABEL: Record<string, string> = {
  llm: '语言模型',
  image: '生图',
  control: '流程控制',
  io: '输入输出',
  transform: '文本处理',
  comic: '漫画',
  general: '通用',
}

export type WorkflowConfigFieldVisibility = 'user' | 'pipeline' | 'template' | 'expert'

type WorkflowConfigFieldDisplay = {
  label: string
  placeholder?: string
  hint?: string
  visibility: WorkflowConfigFieldVisibility
}

const WORKFLOW_CONFIG_FIELD_DISPLAY: Record<string, Record<string, WorkflowConfigFieldDisplay>> = {
  'llm.prompt': {
    model: {
      label: '文本模型',
      visibility: 'user',
    },
    temperature: {
      label: '创造性',
      placeholder: '0.7',
      visibility: 'user',
    },
    platform: { label: '平台', visibility: 'pipeline' },
    promptTemplateKey: {
      label: '提示词模板',
      hint: '优先绑定后台可管理的工作流提示词模板',
      visibility: 'template',
    },
    systemPrompt: {
      label: '系统提示词覆盖',
      hint: '可选；留空时使用模板中的 systemPrompt',
      visibility: 'template',
    },
    userPrompt: {
      label: '用户提示词覆盖',
      hint: '可选；留空时使用模板中的 userPrompt',
      visibility: 'template',
    },
    outputField: { label: '输出字段', visibility: 'pipeline' },
    outputFormat: { label: '输出格式', visibility: 'pipeline' },
  },
  'image.generate': {
    platform: { label: '平台', visibility: 'user' },
    model: {
      label: '图片模型',
      visibility: 'user',
    },
    size: {
      label: '输出尺寸',
      visibility: 'user',
    },
    aspect_ratio: {
      label: '宽高比',
      visibility: 'user',
    },
    referenceImagesField: {
      label: '参考图字段',
      hint: '填写共享状态中的参考图 URL 数组字段名',
      visibility: 'user',
    },
    superResolution: {
      label: '超清输出',
      visibility: 'user',
    },
    promptField: { label: '提示词字段', visibility: 'pipeline' },
    outputField: { label: '输出字段', visibility: 'pipeline' },
  },
  'condition.check': {
    sourceField: {
      label: '检查数据',
      visibility: 'pipeline',
    },
    operator: { label: '分支条件', visibility: 'pipeline' },
    expectValue: {
      label: '比较值',
      visibility: 'pipeline',
    },
    passRoute: { label: '通过路由', visibility: 'pipeline' },
    failRoute: { label: '失败路由', visibility: 'pipeline' },
  },
  'loop.until': {
    sourceField: {
      label: '检查数据',
      visibility: 'pipeline',
    },
    operator: { label: '退出条件', visibility: 'pipeline' },
    expectValue: {
      label: '比较值',
      visibility: 'pipeline',
    },
    maxIterations: {
      label: '最大轮次',
      placeholder: '8',
      visibility: 'user',
    },
    bodyRoute: { label: '循环体路由', visibility: 'pipeline' },
    doneRoute: { label: '退出路由', visibility: 'pipeline' },
  },
  'start.input': {
    inputField: { label: '入口字段', visibility: 'pipeline' },
  },
  'end.output': {
    resultField: { label: '结果字段', visibility: 'pipeline' },
  },
  'text.rewrite': {
    inputField: { label: '输入字段', visibility: 'pipeline' },
    outputField: { label: '输出字段', visibility: 'pipeline' },
    prefix: { label: '前缀', visibility: 'pipeline' },
  },
  'storyboard.draft': {
    inputField: { label: '剧情字段', visibility: 'pipeline' },
    pageCountField: { label: '页数字段', visibility: 'pipeline' },
    outputField: { label: '输出字段', visibility: 'pipeline' },
    planField: { label: '回写规划字段', visibility: 'pipeline' },
  },
  'comic.project.create': {
    workflowPlanField: { label: '规划字段', visibility: 'pipeline' },
    outputField: { label: '项目字段', visibility: 'pipeline' },
    publicIdField: { label: '项目ID字段', visibility: 'pipeline' },
  },
  'comic.episode.generate': {
    projectPublicIdField: { label: '项目ID字段', visibility: 'pipeline' },
    generateScope: { label: '生成范围', visibility: 'user' },
    episodeIndex: { label: '目标话次', hint: '生成范围为“指定话”时生效', visibility: 'user' },
    candidatesPerGeneration: { label: '每页候选数', visibility: 'user' },
    maxRetries: { label: '失败重试次数', visibility: 'user' },
    maxCandidatesStored: { label: '保留候选上限', visibility: 'pipeline' },
    outputField: { label: '输出字段', visibility: 'pipeline' },
  },
}

const DEFAULT_PIPELINE_VISIBILITY: WorkflowConfigFieldVisibility = 'pipeline'

export function workflowDefinitionStatusLabel(status: string): string {
  const key = status.trim().toLowerCase()
  return WORKFLOW_DEFINITION_STATUS_LABEL[key] ?? status
}

export function workflowStepTypeLabel(type: string): string {
  return WORKFLOW_STEP_TYPE_META[type]?.label ?? type
}

export function workflowStepDefaultName(type: string, fallback = ''): string {
  return WORKFLOW_STEP_TYPE_META[type]?.defaultName ?? (fallback || type)
}

export function workflowStepTypeDescription(type: string): string {
  return WORKFLOW_STEP_TYPE_META[type]?.description ?? ''
}

export function workflowStepCategoryLabel(category: string): string {
  const key = category.trim().toLowerCase() || 'general'
  return WORKFLOW_STEP_CATEGORY_LABEL[key] ?? category
}

export { workflowControlOperatorNeedsExpectValue } from './workflowControlFlow'

export function workflowConfigFieldVisibility(nodeType: string, fieldKey: string): WorkflowConfigFieldVisibility {
  return WORKFLOW_CONFIG_FIELD_DISPLAY[nodeType]?.[fieldKey]?.visibility ?? DEFAULT_PIPELINE_VISIBILITY
}

export function workflowConfigFieldDisplay(
  nodeType: string,
  fieldKey: string,
  wireLabel = '',
): Pick<WorkflowConfigFieldDisplay, 'label' | 'placeholder' | 'hint'> {
  const mapped = WORKFLOW_CONFIG_FIELD_DISPLAY[nodeType]?.[fieldKey]
  return {
    label: mapped?.label ?? (wireLabel || fieldKey),
    placeholder: mapped?.placeholder,
    hint: mapped?.hint,
  }
}

export function workflowConfigOptionLabel(nodeType: string, value: string, wireLabel = ''): string {
  if (nodeType === 'condition.check' || nodeType === 'loop.until') {
    return workflowControlOperatorLabel(value) || wireLabel || value
  }
  return wireLabel || value
}

export type WorkflowNodeConfigFormField = {
  key: string
  label: string
  inputType: 'text' | 'textarea' | 'number' | 'select'
  required: boolean
  placeholder: string
  hint: string
  options: { value: string; label: string }[]
}

export type WorkflowImageDynamicConfig = {
  platformOptions: { value: string; label: string }[]
  sizeOptions: { value: string; label: string }[]
  aspectRatioOptions: { value: string; label: string }[]
  extraOptions: ClientUiModelOption[]
  supportsReferenceImages: boolean
}

export function buildWorkflowNodeUserConfigFields(
  catalogItem: WorkflowStepCatalogItem | undefined,
  nodeType: string,
  textModelOptions: { value: string; label: string }[],
  imageModelOptions: { value: string; label: string }[],
  promptTemplateOptions: { value: string; label: string }[] = [],
  config: Record<string, unknown> = {},
  imageConfig: WorkflowImageDynamicConfig | null = null,
): WorkflowNodeConfigFormField[] {
  const schema = catalogItem?.configSchema ?? []
  const fields: WorkflowNodeConfigFormField[] = []

  for (const wire of schema) {
    const visibility = workflowConfigFieldVisibility(nodeType, wire.key)
    if (visibility !== 'user' && visibility !== 'template') continue
    if (wire.key === 'expectValue') {
      const operator = String(config.operator ?? 'not_empty').trim() || 'not_empty'
      if (!workflowControlOperatorNeedsExpectValue(operator)) continue
    }

    const display = workflowConfigFieldDisplay(nodeType, wire.key, wire.label)
    let inputType = wire.inputType
    let options = (wire.options ?? []).map((option) => ({
      value: option.value,
      label: workflowConfigOptionLabel(nodeType, option.value, option.label),
    }))

    if (nodeType === 'llm.prompt' && wire.key === 'model') {
      inputType = 'select'
      options = textModelOptions
    }
    if (nodeType === 'llm.prompt' && wire.key === 'promptTemplateKey') {
      inputType = 'select'
      options = promptTemplateOptions
    }
    if (nodeType === 'image.generate' && wire.key === 'model') {
      inputType = 'select'
      options = imageModelOptions
    }
    if (nodeType === 'image.generate' && wire.key === 'platform') {
      inputType = 'select'
      options = imageConfig?.platformOptions ?? []
    }
    if (nodeType === 'image.generate' && wire.key === 'size') {
      if ((imageConfig?.sizeOptions.length ?? 0) === 0) continue
      inputType = 'select'
      options = imageConfig?.sizeOptions ?? []
    }
    if (nodeType === 'image.generate' && wire.key === 'aspect_ratio') {
      if ((imageConfig?.aspectRatioOptions.length ?? 0) === 0) continue
      inputType = 'select'
      options = imageConfig?.aspectRatioOptions ?? []
    }
    if (nodeType === 'image.generate' && wire.key === 'referenceImagesField' && !imageConfig?.supportsReferenceImages) {
      continue
    }

    fields.push({
      key: wire.key,
      label: display.label,
      inputType,
      required: wire.required,
      placeholder: display.placeholder ?? wire.placeholder ?? '',
      hint: display.hint ?? '',
      options,
    })
  }

  if (nodeType === 'image.generate' && imageConfig?.extraOptions?.length) {
    for (const option of imageConfig.extraOptions) {
      fields.push({
        key: option.key,
        label: option.label,
        inputType:
          option.type === 'number' && (option.options?.length ?? 0) === 0
            ? 'number'
            : 'select',
        required: false,
        placeholder: '',
        hint: '',
        options:
          option.type === 'boolean'
            ? [
                { value: 'false', label: '关闭' },
                { value: 'true', label: '开启' },
              ]
            : (option.options ?? []).map((item) => ({
                value: item.value,
                label: item.label,
              })),
      })
    }
  }

  return fields
}
